import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FeedbackMessage, ReferenceNote, VoiceType, WeeklyPlanDay, WarmupStep } from '@shared/protocol';
import type { VeloraTheme } from '../lib/themes';
import { setLang as i18nSetLang, type Lang } from '../lib/i18n';

export interface PitchPoint {
  timestamp: number;
  frequency: number;
  note: string;
  cents: number;
  stability: number;
}

export interface Achievement {
  id: string;
  label: string;
  icon: string;
  unlockedAt: number | null;
}

const ACHIEVEMENTS_DEF: Achievement[] = [
  { id: 'first_session', label: 'First Session', icon: '🎤', unlockedAt: null },
  { id: 'streak_7', label: '7-Day Streak', icon: '🔥', unlockedAt: null },
  { id: 'streak_30', label: '30-Day Streak', icon: '💫', unlockedAt: null },
  { id: 'notes_100', label: 'Hit 100 Notes', icon: '🎵', unlockedAt: null },
  { id: 'notes_1000', label: '1000 Notes In Tune', icon: '🏆', unlockedAt: null },
  { id: 'full_octave', label: 'Full Octave Range', icon: '🎼', unlockedAt: null },
  { id: 'perfect_session', label: 'Perfect Session (90%+)', icon: '⭐', unlockedAt: null },
  { id: 'range_test', label: 'Know Your Voice', icon: '🎭', unlockedAt: null },
];

interface VeloraState {
  // Session
  isRecording: boolean;
  sessionStartTime: number | null;
  sessionCount: number;

  // Pitch
  currentFrequency: number;
  currentNote: string;
  currentCents: number;
  stability: number;
  pitchHistory: PitchPoint[];

  // Score
  score: number;
  totalFrames: number;
  inTuneFrames: number;
  allTimeNotesInTune: number;

  // Feedback from desktop AI
  feedbackMessages: FeedbackMessage[];

  // Connection
  theme: VeloraTheme;
  setTheme: (t: VeloraTheme) => void;
  lang: Lang;
  setLang: (l: Lang) => void;
  desktopHost: string;
  isConnected: boolean;

  // Reference track
  referenceNotes: ReferenceNote[];
  transposeSemitones: number;

  // Settings
  wsEnabled: boolean;

  // Streak
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string | null;

  // Achievements
  achievements: Achievement[];
  pendingAchievement: Achievement | null;

  // Vocal range
  voiceType: VoiceType | null;
  lowestHz: number | null;
  highestHz: number | null;
  lowestNote: string | null;
  highestNote: string | null;

  // Weekly plan from AI
  weeklyPlan: WeeklyPlanDay[] | null;
  weeklyPlanDate: string | null;

  // Warmup
  warmupSteps: WarmupStep[] | null;

  // Metronome
  bpm: number;
  metronomeOn: boolean;

  // Favorites
  favoriteSongs: string[];

  // Karaoke score streak
  inTuneStreak: number;
  bestStreak: number;

  // Onboarding
  onboarded: boolean;
  userName: string;
  singingStyles: string[];
  trainingGoal: string;
  setOnboarded: (v: boolean) => void;
  setUserName: (n: string) => void;
  setSingingStyles: (s: string[]) => void;
  setTrainingGoal: (g: string) => void;

  // Actions
  startRecording: () => void;
  stopRecording: () => void;
  updatePitch: (freq: number, note: string, cents: number, stability: number) => void;
  addFeedback: (msg: FeedbackMessage) => void;
  dismissFeedback: (id: string) => void;
  setConnected: (v: boolean) => void;
  setDesktopHost: (host: string) => void;
  setTranspose: (semitones: number) => void;
  setWsEnabled: (v: boolean) => void;
  setReferenceNotes: (notes: ReferenceNote[]) => void;
  setVocalRange: (lowest: number, highest: number, lowestNote: string, highestNote: string, type: VoiceType) => void;
  setWeeklyPlan: (plan: WeeklyPlanDay[]) => void;
  setWarmupSteps: (steps: WarmupStep[]) => void;
  setBpm: (bpm: number) => void;
  toggleMetronome: () => void;
  checkDailyStreak: () => void;
  unlockAchievement: (id: string) => void;
  dismissAchievement: () => void;
  toggleFavorite: (id: string) => void;
  reset: () => void;
}

const MAX_HISTORY = 300;

export const useVeloraStore = create<VeloraState>()(
  persist(
    (set, get) => ({
      isRecording: false,
      sessionStartTime: null,
      sessionCount: 0,
      currentFrequency: 0,
      currentNote: '--',
      currentCents: 0,
      stability: 0,
      pitchHistory: [],
      score: 0,
      totalFrames: 0,
      inTuneFrames: 0,
      allTimeNotesInTune: 0,
      feedbackMessages: [],
      theme: 'dark' as VeloraTheme,
      setTheme: (t) => set({ theme: t }),
      lang: 'en' as Lang,
      setLang: (l) => { set({ lang: l }); i18nSetLang(l); },
      desktopHost: '192.168.1.100',
      isConnected: false,
      referenceNotes: [],
      transposeSemitones: 0,
      wsEnabled: false,
      currentStreak: 0,
      longestStreak: 0,
      lastPracticeDate: null,
      achievements: ACHIEVEMENTS_DEF,
      pendingAchievement: null,
      voiceType: null,
      lowestHz: null,
      highestHz: null,
      lowestNote: null,
      highestNote: null,
      weeklyPlan: null,
      weeklyPlanDate: null,
      warmupSteps: null,
      bpm: 80,
      metronomeOn: false,
      favoriteSongs: [],
      inTuneStreak: 0,
      bestStreak: 0,

      onboarded: false,
      userName: '',
      singingStyles: [],
      trainingGoal: '',
      setOnboarded: (v) => set({ onboarded: v }),
      setUserName: (n) => set({ userName: n }),
      setSingingStyles: (s) => set({ singingStyles: s }),
      setTrainingGoal: (g) => set({ trainingGoal: g }),

      startRecording: () => set(_s => ({
        isRecording: true,
        sessionStartTime: Date.now(),
        pitchHistory: [],
        score: 0,
        totalFrames: 0,
        inTuneFrames: 0,
        inTuneStreak: 0,
      })),

      stopRecording: () => {
        const { allTimeNotesInTune, inTuneFrames, score, sessionCount } = get();
        const newTotal = allTimeNotesInTune + inTuneFrames;
        const newCount = sessionCount + 1;
        set({ isRecording: false, allTimeNotesInTune: newTotal, sessionCount: newCount });

        const store = get();
        if (newCount === 1) store.unlockAchievement('first_session');
        if (newTotal >= 100) store.unlockAchievement('notes_100');
        if (newTotal >= 1000) store.unlockAchievement('notes_1000');
        if (score >= 90) store.unlockAchievement('perfect_session');
        store.checkDailyStreak();

        import('../lib/dailyChallenge').then(({ completeDailyChallenge }) => {
          completeDailyChallenge(score);
        });
        import('../lib/notify').then(({ notify }) => {
          const label = score >= 90 ? '🌟 Perfect!' : score >= 70 ? '👍 Great!' : '🎤 Done!';
          notify('Velora', `${label} Session score: ${score}%`).catch(() => {});
        });
      },

      updatePitch: (freq, note, cents, stability) => {
        const { pitchHistory, totalFrames, inTuneFrames, inTuneStreak, bestStreak } = get();
        const point: PitchPoint = { timestamp: Date.now(), frequency: freq, note, cents, stability };
        const newHistory = [...pitchHistory, point].slice(-MAX_HISTORY);
        const newTotal = totalFrames + 1;
        const isInTune = Math.abs(cents) <= 15;
        const newInTune = inTuneFrames + (isInTune ? 1 : 0);
        const newStreak = isInTune ? inTuneStreak + 1 : 0;
        const newBestStreak = Math.max(bestStreak, newStreak);
        set({
          currentFrequency: freq,
          currentNote: note,
          currentCents: cents,
          stability,
          pitchHistory: newHistory,
          totalFrames: newTotal,
          inTuneFrames: newInTune,
          score: newTotal > 0 ? Math.round((newInTune / newTotal) * 100) : 0,
          inTuneStreak: newStreak,
          bestStreak: newBestStreak,
        });
      },

      addFeedback: (msg) => set(s => ({
        feedbackMessages: [msg, ...s.feedbackMessages].slice(0, 5),
      })),
      dismissFeedback: (id) => set(s => ({
        feedbackMessages: s.feedbackMessages.filter(m => m.id !== id),
      })),

      setConnected: (v) => set({ isConnected: v }),
      setDesktopHost: (host) => set({ desktopHost: host }),
      setTranspose: (semitones) => set({ transposeSemitones: semitones }),
      setWsEnabled: (v) => set({ wsEnabled: v }),
      setReferenceNotes: (notes) => set({ referenceNotes: notes }),
      setBpm: (bpm) => set({ bpm }),
      toggleMetronome: () => set(s => ({ metronomeOn: !s.metronomeOn })),

      setVocalRange: (lowest, highest, lowestNote, highestNote, type) => {
        set({ lowestHz: lowest, highestHz: highest, lowestNote, highestNote, voiceType: type });
        get().unlockAchievement('range_test');
        const semitones = Math.round(12 * Math.log2(highest / lowest));
        if (semitones >= 12) get().unlockAchievement('full_octave');
      },

      setWeeklyPlan: (plan) => set({ weeklyPlan: plan, weeklyPlanDate: new Date().toISOString().slice(0, 10) }),
      setWarmupSteps: (steps) => set({ warmupSteps: steps }),

      checkDailyStreak: () => {
        const today = new Date().toISOString().slice(0, 10);
        const { lastPracticeDate, currentStreak, longestStreak } = get();
        if (lastPracticeDate === today) return;

        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        const newStreak = lastPracticeDate === yesterday ? currentStreak + 1 : 1;
        const newLongest = Math.max(longestStreak, newStreak);

        set({ currentStreak: newStreak, longestStreak: newLongest, lastPracticeDate: today });

        if (newStreak >= 7) get().unlockAchievement('streak_7');
        if (newStreak >= 30) get().unlockAchievement('streak_30');
      },

      unlockAchievement: (id) => {
        const achievements = get().achievements;
        const ach = achievements.find(a => a.id === id);
        if (!ach || ach.unlockedAt) return;
        const updated = achievements.map(a => a.id === id ? { ...a, unlockedAt: Date.now() } : a);
        set({ achievements: updated, pendingAchievement: { ...ach, unlockedAt: Date.now() } });
      },

      dismissAchievement: () => set({ pendingAchievement: null }),

      toggleFavorite: (id) => set(s => ({
        favoriteSongs: s.favoriteSongs.includes(id)
          ? s.favoriteSongs.filter(f => f !== id)
          : [...s.favoriteSongs, id],
      })),

      reset: () => set({
        isRecording: false,
        sessionStartTime: null,
        currentFrequency: 0,
        currentNote: '--',
        currentCents: 0,
        stability: 0,
        pitchHistory: [],
        score: 0,
        totalFrames: 0,
        inTuneFrames: 0,
        feedbackMessages: [],
        inTuneStreak: 0,
      }),
    }),
    {
      name: 'velora-storage',
      partialize: (s) => ({
        desktopHost: s.desktopHost,
        transposeSemitones: s.transposeSemitones,
        wsEnabled: s.wsEnabled,
        currentStreak: s.currentStreak,
        longestStreak: s.longestStreak,
        lastPracticeDate: s.lastPracticeDate,
        achievements: s.achievements,
        allTimeNotesInTune: s.allTimeNotesInTune,
        sessionCount: s.sessionCount,
        voiceType: s.voiceType,
        lowestHz: s.lowestHz,
        highestHz: s.highestHz,
        lowestNote: s.lowestNote,
        highestNote: s.highestNote,
        bpm: s.bpm,
        weeklyPlan: s.weeklyPlan,
        weeklyPlanDate: s.weeklyPlanDate,
        favoriteSongs: s.favoriteSongs,
        bestStreak: s.bestStreak,
      }),
    }
  )
);
